/**
 * Phase 98y — Historique syncs & diagnostics Nayax.
 *
 * Consomme :
 *   GET  /api/nayax/picklist-logs/stats
 *   GET  /api/nayax/picklist-logs?ok=false&days=7&limit=200
 *   GET  /api/nayax/picklist-logs/{id}         (détail avec produits + étapes)
 *   POST /api/nayax/picklist-logs/{id}/replay  (nécessite double confirmation)
 *   DELETE /api/nayax/picklist-logs/{id}
 *   GET  /api/nayax/picklist-logs.csv?days=30  (export)
 *
 * Filtres : statut, source, machine, période.
 * Détail : dialog avec les 3 étapes, body_excerpt, action Rejouer conditionnelle.
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, XCircle, Download, RefreshCw, Loader2, Trash2, Play } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useConfirm } from "@/components/ConfirmDialog";

const SOURCE_LABEL = {
  "lock-and-print": "Verrouillage tournée",
  "sync-to-nayax": "Sync manuelle",
  "test-write": "Diagnostic (probe)",
  "replay": "Rejeu",
};

const STATUS_LABEL = {
  200: "OK",
  204: "OK (No Content)",
  401: "Token invalide",
  403: "Rôle manquant",
  404: "Machine introuvable",
  500: "Erreur Nayax",
  502: "Bad Gateway",
  503: "Service Unavailable",
};

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function NayaxSyncLogs() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [statusFilter, setStatusFilter] = useState("all"); // all / ok / ko
  const [sourceFilter, setSourceFilter] = useState("all");
  const [detailLog, setDetailLog] = useState(null);
  const confirm = useConfirm();

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/nayax/picklist-logs/stats");
      setStats(data);
    } catch (e) {
      // silent
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days), limit: "300" });
      if (statusFilter !== "all") params.set("ok", statusFilter === "ok" ? "true" : "false");
      if (sourceFilter !== "all") params.set("source", sourceFilter);
      const { data } = await api.get(`/nayax/picklist-logs?${params}`);
      setLogs(data.logs || []);
    } catch (e) {
      toast.error("Erreur chargement historique");
    } finally {
      setLoading(false);
    }
  }, [days, statusFilter, sourceFilter]);

  useEffect(() => {
    loadStats();
    load();
  }, [load, loadStats]);

  const openDetail = async (logId) => {
    try {
      const { data } = await api.get(`/nayax/picklist-logs/${logId}`);
      setDetailLog(data);
    } catch (e) {
      toast.error("Erreur récupération détail");
    }
  };

  const doReplay = async (log) => {
    // Rejouer désactivé par défaut : double confirmation explicite requise
    const ok = await confirm({
      title: "⚠️ Rejouer ce sync ?",
      description:
        `Vous allez ré-envoyer ${(log.products_input || log.products_pushed || 0)} produits à la machine ${log.nayax_machine_id}.\n\n` +
        "Utilisez UNIQUEMENT pour un échec de transport (401 token, 502 réseau, 404 machine offline).\n\n" +
        "Ne rejouez PAS si :\n" +
        "  • la tournée a été annulée ou refaite depuis\n" +
        "  • les quantités ne sont plus valides\n" +
        "  • vous n'êtes pas sûr\n\n" +
        "En cas d'usage à tort, le stock côté Nayax sera doublé (à corriger via une nouvelle tournée d'ajustement).",
      confirmText: "Je confirme que les données sont valides",
      destructive: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/nayax/picklist-logs/${log.id}/replay`, {
        confirm_data_still_valid: true,
        enable_replay: true,
      });
      if (data.ok) {
        toast.success(`Rejeu OK — ${data.products_pushed || 0} produits poussés`);
      } else {
        toast.error(`Rejeu KO — ${data.verdict || data.error || `HTTP ${data.http_status}`}`);
      }
      await load();
      await loadStats();
      setDetailLog(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur rejeu");
    }
  };

  const doDelete = async (log) => {
    const ok = await confirm({
      title: "Supprimer ce log ?",
      description: "Le log est supprimé de la base (ne rembobine pas l'action Nayax qu'il représente).",
      confirmText: "Supprimer",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/nayax/picklist-logs/${log.id}`);
      toast.success("Log supprimé");
      await load();
      await loadStats();
      setDetailLog(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur suppression");
    }
  };

  const doExportCsv = () => {
    // Ouvre dans un nouvel onglet, le navigateur télécharge le fichier
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/nayax/picklist-logs.csv?days=30`;
    window.open(url, "_blank");
  };

  return (
    <div className="p-6 space-y-4 max-w-[1400px] mx-auto" data-testid="nayax-sync-logs-page">
      {/* Header + Stats */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[#1F4E3D]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Historique syncs Nayax
          </h1>
          <p className="text-xs text-[#575A57] mt-1">
            Traces des appels pickList (verrouillage, sync manuelle, diagnostics). Rétention 30 jours.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => { load(); loadStats(); }} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Rafraîchir
          </Button>
          <Button size="sm" variant="outline" onClick={doExportCsv} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV (30j)
          </Button>
        </div>
      </div>

      {/* Bannière alerte dégradé */}
      {stats?.degraded && (
        <div className="p-3 bg-red-50 border border-red-300 rounded flex items-start gap-2" data-testid="degraded-banner">
          <AlertCircle className="w-4 h-4 text-red-700 shrink-0 mt-0.5" />
          <div className="text-sm text-red-900">
            <strong>Sync Nayax dégradé</strong> — {stats.last_24h.ko} échec(s) sur les dernières 24h.
            Vérifiez si un token a expiré ou si une machine est offline.
          </div>
        </div>
      )}

      {/* Cards stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="OK (24h)" value={stats.last_24h.ok} color="text-emerald-700" bg="bg-emerald-50" icon={CheckCircle2} />
          <StatCard label="KO (24h)" value={stats.last_24h.ko} color="text-red-700" bg="bg-red-50" icon={XCircle} />
          <StatCard label="OK (7j)" value={stats.last_7d.ok} color="text-emerald-600" bg="bg-white" icon={CheckCircle2} border />
          <StatCard label="KO (7j)" value={stats.last_7d.ko} color="text-red-600" bg="bg-white" icon={XCircle} border />
        </div>
      )}

      {/* Filtres */}
      <Card className="border-[#E5E5E0]">
        <CardContent className="p-3 flex gap-3 flex-wrap items-center">
          <label className="flex items-center gap-2 text-xs">
            <span className="text-[#575A57]">Période :</span>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="border border-[#E5E5E0] rounded px-2 py-1 text-sm"
              data-testid="filter-days"
            >
              <option value={1}>1 jour</option>
              <option value={7}>7 jours</option>
              <option value={30}>30 jours</option>
              <option value={90}>90 jours</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs">
            <span className="text-[#575A57]">Statut :</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-[#E5E5E0] rounded px-2 py-1 text-sm"
              data-testid="filter-status"
            >
              <option value="all">Tous</option>
              <option value="ok">OK uniquement</option>
              <option value="ko">Échecs uniquement</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs">
            <span className="text-[#575A57]">Source :</span>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="border border-[#E5E5E0] rounded px-2 py-1 text-sm"
              data-testid="filter-source"
            >
              <option value="all">Toutes</option>
              <option value="lock-and-print">Verrouillage tournée</option>
              <option value="sync-to-nayax">Sync manuelle</option>
              <option value="test-write">Diagnostic (probe)</option>
              <option value="replay">Rejeu</option>
            </select>
          </label>
          <span className="text-xs text-[#6B6E6B] ml-auto">
            {logs.length} log(s) affiché(s)
          </span>
        </CardContent>
      </Card>

      {/* Tableau */}
      <Card className="border-[#E5E5E0]">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="logs-table">
              <thead className="bg-[#F4F4F0] text-[#575A57] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left p-2 font-medium">Date</th>
                  <th className="text-left p-2 font-medium">Source</th>
                  <th className="text-left p-2 font-medium">Distributeur</th>
                  <th className="text-right p-2 font-medium">Machine</th>
                  <th className="text-center p-2 font-medium">Statut</th>
                  <th className="text-center p-2 font-medium">HTTP</th>
                  <th className="text-center p-2 font-medium">Produits</th>
                  <th className="text-left p-2 font-medium">Détail</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="p-8 text-center text-[#6B6E6B]"><Loader2 className="w-5 h-5 animate-spin inline-block" /></td></tr>
                ) : logs.length === 0 ? (
                  <tr><td colSpan={8} className="p-8 text-center text-[#6B6E6B]">Aucun log sur la période sélectionnée</td></tr>
                ) : (
                  logs.map((log) => (
                    <tr
                      key={log.id}
                      className="border-t border-[#E5E5E0] hover:bg-[#FAFAF7] cursor-pointer"
                      onClick={() => openDetail(log.id)}
                      data-testid={`log-row-${log.id}`}
                    >
                      <td className="p-2 whitespace-nowrap font-mono text-[11px]">{fmtDate(log.created_at)}</td>
                      <td className="p-2 text-xs">{SOURCE_LABEL[log.source] || log.source}</td>
                      <td className="p-2 text-xs">{log.distributor_name || <span className="text-[#6B6E6B]">—</span>}</td>
                      <td className="p-2 font-mono text-[11px] text-right">{log.nayax_machine_id || ""}</td>
                      <td className="p-2 text-center">
                        {log.ok ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                            <CheckCircle2 className="w-3.5 h-3.5" /> OK
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-700 text-xs">
                            <XCircle className="w-3.5 h-3.5" /> KO
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-center font-mono text-[11px]">
                        {log.http_status || "—"}
                        {log.http_status && STATUS_LABEL[log.http_status] && (
                          <span className="block text-[9px] text-[#6B6E6B]">{STATUS_LABEL[log.http_status]}</span>
                        )}
                      </td>
                      <td className="p-2 text-center font-mono text-[11px]">
                        {log.products_pushed || 0}
                        {log.products_unmapped > 0 && (
                          <span className="block text-[9px] text-orange-700">
                            +{log.products_unmapped} non mappés
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-xs text-[#575A57] max-w-[400px] truncate">
                        {log.verdict || log.error || log.step_failed || (log.ok ? "Succès" : "Échec")}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog détail */}
      <Dialog open={!!detailLog} onOpenChange={(o) => { if (!o) setDetailLog(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="log-detail-dialog">
          {detailLog && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detailLog.ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
                  <span className="font-mono text-sm">{fmtDate(detailLog.created_at)}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-[#F4F4F0] text-[#575A57]">
                    {SOURCE_LABEL[detailLog.source] || detailLog.source}
                  </span>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-3 text-sm">
                {/* Contexte */}
                <div className="grid grid-cols-2 gap-3 text-[13px]">
                  <div><span className="text-[#6B6E6B]">Distributeur :</span> <strong>{detailLog.distributor_name || "—"}</strong></div>
                  <div><span className="text-[#6B6E6B]">Machine ID :</span> <span className="font-mono">{detailLog.nayax_machine_id || "—"}</span></div>
                  <div><span className="text-[#6B6E6B]">HTTP final :</span> <span className="font-mono">{detailLog.http_status || "—"}</span></div>
                  <div><span className="text-[#6B6E6B]">Env :</span> <span className="font-mono uppercase">{detailLog.env || "—"}</span></div>
                  {detailLog.tour_id && (
                    <div className="col-span-2 text-xs">
                      <span className="text-[#6B6E6B]">Tour :</span> <span className="font-mono">{detailLog.tour_id}</span>
                    </div>
                  )}
                </div>

                {/* Verdict / erreur */}
                {(detailLog.verdict || detailLog.error) && (
                  <div className={`p-2 rounded text-xs ${detailLog.ok ? "bg-emerald-50 text-emerald-900" : "bg-red-50 text-red-900"}`}>
                    {detailLog.verdict || detailLog.error}
                  </div>
                )}

                {/* Étapes */}
                {(detailLog.steps || []).length > 0 && (
                  <div>
                    <h3 className="font-semibold text-[11px] uppercase tracking-wider text-[#575A57] mb-1">Étapes</h3>
                    <div className="space-y-1">
                      {detailLog.steps.map((s, i) => (
                        <div key={i} className={`p-2 rounded text-xs border ${s.http_status >= 400 ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"}`}>
                          <div className="flex justify-between">
                            <strong>{i + 1}. {s.step}</strong>
                            <span className="font-mono">HTTP {s.http_status}</span>
                          </div>
                          <div className="font-mono text-[10px] break-all text-[#575A57] mt-0.5">{s.url}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Produits envoyés */}
                {(detailLog.products_input || []).length > 0 && (
                  <div>
                    <h3 className="font-semibold text-[11px] uppercase tracking-wider text-[#575A57] mb-1">
                      Produits envoyés ({detailLog.products_input.length})
                    </h3>
                    <div className="max-h-40 overflow-y-auto border border-[#E5E5E0] rounded">
                      <table className="w-full text-xs">
                        <thead className="bg-[#F4F4F0]">
                          <tr>
                            <th className="text-left p-1.5">NayaxProductID</th>
                            <th className="text-right p-1.5">PickQty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailLog.products_input.map((p, i) => (
                            <tr key={i} className="border-t border-[#E5E5E0]">
                              <td className="p-1.5 font-mono">{p.NayaxProductID}</td>
                              <td className="p-1.5 text-right font-mono">{p.PickQty}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Body excerpt */}
                {detailLog.body_excerpt && (
                  <div>
                    <h3 className="font-semibold text-[11px] uppercase tracking-wider text-[#575A57] mb-1">Réponse Nayax (extrait)</h3>
                    <pre className="p-2 bg-[#1A1C1A] text-[#F4F4F0] rounded text-[10px] overflow-x-auto max-h-32">
                      {detailLog.body_excerpt}
                    </pre>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 justify-end pt-3 border-t border-[#E5E5E0]">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => doDelete(detailLog)}
                  className="text-red-700 hover:bg-red-50"
                  data-testid="delete-log-btn"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
                </Button>
                {!detailLog.ok && (detailLog.products_input || []).length > 0 && (
                  <Button
                    size="sm"
                    onClick={() => doReplay(detailLog)}
                    className="bg-[#C05C47] hover:bg-[#A04E2C] text-white"
                    data-testid="replay-log-btn"
                  >
                    <Play className="w-3.5 h-3.5 mr-1" /> Rejouer
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, color, bg, icon: Icon, border }) {
  return (
    <div className={`${bg} ${border ? "border border-[#E5E5E0]" : ""} rounded p-3 flex items-center gap-3`}>
      <Icon className={`w-6 h-6 ${color}`} />
      <div>
        <div className={`text-2xl font-semibold ${color}`}>{value}</div>
        <div className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">{label}</div>
      </div>
    </div>
  );
}
